import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Modal from '../components/Modal';
import { ClassesComponent } from './Classes';
import PromptHierarchy from '../components/PromptHierarchy';

// Known system message keys and their default texts from the bot
// Список синхронизирован с bot/app/services/message_service.py
const systemMessages = {
  bot_intro: 'Привет, {first_name}! Добро пожаловать в бота сервиса «Иван Везёт» — вашего надежного партнера по доставке товаров из Китая.',
  contact_required: 'ВНИМАНИЕ! Предоставление номера телефона ОБЯЗАТЕЛЬНО для работы с нашим сервисом.',
  contact_success: 'Отлично! Номер телефона {phone} сохранен. Теперь вы можете пользоваться всеми возможностями нашего сервиса!',
  cost_calculation: 'Расчёт стоимости доставки. Для расчёта стоимости доставки из Китая укажите примерный вес вашего груза.',
  main_menu: 'Главное меню. Выберите интересующий раздел:',
  delivery_info: 'Доставка товаров из Китая. Мы предлагаем полный цикл услуг по доставке.',
  moto_info: 'Мототехника из Китая. Специализируемся на доставке мотоциклов, квадроциклов, скутеров.',
  payment_info: 'Помощь с оплатой в Китае. Мы поможем вам с оплатой товаров китайским поставщикам.',
  about_company: 'О компании "Иван Везёт". Мы — команда профессионалов с многолетним опытом доставки товаров из Китая.',
  contacts: 'Контакты "Иван Везёт". WhatsApp: +7 (908) 966-06-66, Telegram: @ivanvezet',
  error_general: 'Произошла ошибка. Попробуйте позже.',
  error_no_access: 'Для доступа к функциям бота необходимо пройти регистрацию. Используйте команду /start',
  chat_started: 'Чат с менеджером начался. Теперь вы можете общаться напрямую.',
  chat_ended: 'Чат завершен. Спасибо за обращение!',
  feedback_request: 'Пожалуйста, оставьте отзыв о качестве обслуживания.',
  chat_timeout_warning: 'Чат будет автоматически завершен через некоторое время из-за неактивности.',
  manager_transfer_notice: 'Ваш запрос передан другому менеджеру.',
  activation: 'Активация аккаунта завершена.',
  tg_channel: 'Для полного доступа к функциям бота необходимо подписаться на наш канал.',
};
// Main component for the entire page
function PromptsContentPage() {
  const { user } = useAuth();
  const isSMMManager = user?.role === 'smm_manager';
  
  // Загружаем сохраненную вкладку из localStorage или используем 'prompts' по умолчанию
  // Для SMM менеджеров всегда используем 'qa' (База знаний)
  const [activeTab, setActiveTab] = useState(() => {
    if (isSMMManager) {
      return 'qa';
    }
    const savedTab = localStorage.getItem('promptsActiveTab');
    return savedTab || 'prompts';
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    example_text: '',
  });
  const addClassRef = useRef(null);
  const addExampleRef = useRef(null);

  // Сохраняем выбранную вкладку в localStorage при изменении
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('promptsActiveTab', tab);
  };

  const handleAddMessage = () => {
    setShowModal(true);
  };

  const handleAddClass = () => {
    if (addClassRef.current) {
      addClassRef.current();
    } else {
      setShowModal(true);
    }
  };

  const handleAddExample = () => {
    if (addExampleRef.current) {
      addExampleRef.current();
    } else {
      setShowModal(true);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Промпты и контент</h2>
        {activeTab === 'messages' && !isSMMManager && (
          <div className="flex gap-2">
            <button onClick={handleAddMessage} className="btn btn-primary">Добавить сообщение</button>
          </div>
        )}
        {activeTab === 'qa' && (
          <div className="flex gap-2">
            <button onClick={handleAddExample} className="btn btn-primary">Добавить вопрос-ответ</button>
          </div>
        )}
        {activeTab === 'classes' && !isSMMManager && (
          <div className="flex gap-2">
            <button onClick={handleAddClass} className="btn btn-primary">Добавить класс</button>
          </div>
        )}
      </div>
      <div className="tabs">
        {!isSMMManager && (
          <button
            className={`tab ${activeTab === 'prompts' ? 'active' : ''}`}
            onClick={() => handleTabChange('prompts')}
          >
            Промпты
          </button>
        )}
        {!isSMMManager && (
          <button
            className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => handleTabChange('messages')}
          >
            Сообщения бота
          </button>
        )}
        <button
          className={`tab ${activeTab === 'qa' ? 'active' : ''}`}
          onClick={() => handleTabChange('qa')}
        >
          База знаний
        </button>
        {!isSMMManager && (
          <button
            className={`tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => handleTabChange('classes')}
          >
            Классификатор
          </button>
        )}
      </div>

      <div className="tab-content">
        {activeTab === 'messages' && <BotMessagesComponent showModal={showModal} setShowModal={setShowModal} selectedItem={selectedItem} setSelectedItem={setSelectedItem} formData={formData} setFormData={setFormData} />}
        {activeTab === 'prompts' && <QnaComponent onTabChange={setActiveTab} />}
        {activeTab === 'qa' && <CompanyInfoExamplesComponent onAddExampleRef={addExampleRef} />}
        {activeTab === 'classes' && <ClassesComponent showModal={showModal} setShowModal={setShowModal} selectedClass={selectedItem} setSelectedClass={setSelectedItem} formData={formData} setFormData={setFormData} onAddClassRef={addClassRef} />}
      </div>
    </div>
  );
}

// Component for the "Bot Messages" tab
function BotMessagesComponent({ showModal, setShowModal, selectedItem, setSelectedItem, formData, setFormData }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [originalMessageText, setOriginalMessageText] = useState('');

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getMessages();
      setMessages(data);
    } catch (error) {
      toast.error('Ошибка загрузки сообщений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({
      name: '',
      message_text: '',
      message_number: 1,
      is_active: true,
    });
    setShowModal(true);
  };

  const handleEdit = async (item) => {
    try {
      const messageWithVersions = await api.getMessage(item.id);
      setSelectedItem(messageWithVersions);
      setFormData({ ...messageWithVersions });

      // Загружаем все версии для dropdown
      if (messageWithVersions.versions && messageWithVersions.versions.length > 0) {
        setAvailableVersions(messageWithVersions.versions);

        // Находим активную версию
        const activeVersion = messageWithVersions.versions.find(v => v.is_active);
        if (activeVersion) {
          setSelectedVersionId(activeVersion.id);
          setOriginalMessageText(activeVersion.message_text);
          // Устанавливаем текст активной версии в formData
          setFormData(prev => ({ ...prev, message_text: activeVersion.message_text }));
        }
      } else {
        setAvailableVersions([]);
        setSelectedVersionId(null);
        setOriginalMessageText('');
      }

      setShowModal(true);
    } catch (error) {
      toast.error('Ошибка загрузки данных сообщения');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это сообщение?')) return;
    try {
      await api.deleteMessage(itemId);
      toast.success('Сообщение удалено');
      fetchMessages();
    } catch (error) {
      toast.error(error.message || 'Ошибка при удалении сообщения');
    }
  };

  // Обработчик выбора версии из dropdown
  const handleVersionChange = async (versionId) => {
    const versionIdNum = parseInt(versionId);
    const selectedVersion = availableVersions.find(v => v.id === versionIdNum);

    if (!selectedVersion) return;

    try {
      // Активируем выбранную версию
      await api.activateMessageVersion(selectedItem.id, versionIdNum);
      toast.success(`Активирована версия v${selectedVersion.version}`);

      // Обновляем состояние
      setSelectedVersionId(versionIdNum);
      setOriginalMessageText(selectedVersion.message_text);
      setFormData(prev => ({ ...prev, message_text: selectedVersion.message_text }));

      // Обновляем список версий (is_active флаги)
      const updatedMessageData = await api.getMessage(selectedItem.id);
      setAvailableVersions(updatedMessageData.versions);
      fetchMessages(); // Обновляем таблицу
    } catch (error) {
      toast.error(error.message || 'Ошибка активации версии');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || !formData.message_text || formData.message_number === '') {
      toast.error('Поля Название, Текст и Номер сообщения обязательны');
      return;
    }

    try {
      // Проверяем изменился ли текст сообщения (только для редактирования)
      const messageTextChanged = selectedItem &&
                           formData.message_text &&
                           formData.message_text.trim() !== '' &&
                           formData.message_text.trim() !== originalMessageText.trim();

      // 1. Если изменился текст - создаем новую версию
      if (messageTextChanged) {
        await api.createMessageVersion(selectedItem.id, {
          message_text: formData.message_text.trim(),
          version_notes: 'Обновлено через редактор сообщений'
        });
        toast.success('✅ Создана новая версия сообщения');
      }

      // 2. Обновляем метаданные сообщения (название, номер, is_active)
      const payload = {
        name: formData.name.trim(),
        message_number: parseInt(formData.message_number, 10),
        // Для создания сообщения отправляем текст, для обновления - нет (версии управляются отдельно)
        message_text: !selectedItem && formData.message_text ? formData.message_text.trim() : undefined,
        is_active: formData.is_active,
      };

      const apiCall = selectedItem
        ? api.updateMessage(selectedItem.id, payload)
        : api.createMessage(payload);

      await apiCall;

      if (!messageTextChanged) {
        toast.success(selectedItem ? 'Сообщение обновлено' : 'Сообщение создано');
      }

      setShowModal(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSystemMessageSelect = (name) => {
    if (!name) {
        // При сбросе select не очищаем formData, так как пользователь может ввести свое название
        return;
    }
    const existingMessage = messages.find(m => m.name === name);
    if (existingMessage) {
        handleEdit(existingMessage);
    } else {
        setFormData(prev => ({
            ...prev,
            name: name,
            message_text: systemMessages[name] || '',
            message_number: 1,
            is_active: true,
        }));
    }
  }

  const availableSystemKeys = Object.keys(systemMessages).filter(
    key => !messages.some(msg => msg.name === key)
  );

  return (
    <div>
      {loading ? (
        <p>Загрузка сообщений...</p>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Номер сортировки</th>
                <th>Текст сообщения</th>
                <th>Версия</th>
                <th>Статус</th>
                <th className="w-20 text-center">Действия</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((item) => (
                <tr key={item.id}>
                  <td>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">{item.name}</span>
                  </td>
                  <td className="text-center">{item.message_number}</td>
                  <td className="text-sm text-gray-600" title={item.message_text}>{item.message_text.substring(0, 70)}...</td>
                  <td className="text-sm">
                    {item.active_version !== null && item.active_version !== undefined ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        v{item.active_version}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${item.is_active ? 'status-active' : 'status-inactive'}`}>
                      {item.is_active ? 'Активно' : 'Неактивно'}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="table-actions">
                      <button onClick={() => handleEdit(item)} className="btn btn-outline btn-small">Редактировать</button>
                      <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-small">Удалить</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedItem ? 'Редактировать сообщение' : 'Добавить сообщение'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedItem ? (
              <div className="form-group">
                <label>Название</label>
                <input type="text" disabled value={formData.name || ''} />
              </div>
            ) : (
              <div className="form-group">
                <label>Название системного сообщения</label>
                <select
                  value={systemMessages[formData.name] ? formData.name : ""}
                  onChange={(e) => handleSystemMessageSelect(e.target.value)}
                >
                  <option value="">-- Выберите системное сообщение --</option>
                  {availableSystemKeys.map(key => (
                    <option key={key} value={key}>{key}</option>
                  ))}
                </select>

                {formData.name && systemMessages[formData.name] && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>
                      Превью системного сообщения:
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}>
                      {systemMessages[formData.name]}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-1">Или создайте новое пользовательское сообщение, введя свое название ниже.</p>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Или введите свое название..."
                  className="mt-2"
                />
              </div>
            )}

            <div className="form-group">
              <label>Номер сортировки *</label>
              <input type="number" required value={formData.message_number || ''} onChange={(e) => handleFormChange('message_number', e.target.value)} placeholder="1" />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>Номер определяет порядок отображения сообщения. Разные номера позволяют иметь несколько вариантов одного сообщения.</small>
            </div>

            {/* Если редактируем сообщение и есть версии - показываем dropdown */}
            {selectedItem && availableVersions.length > 0 ? (
              <div className="form-group">
                <label>Версия сообщения</label>
                <select
                  value={selectedVersionId || ''}
                  onChange={(e) => handleVersionChange(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                >
                  {availableVersions.map(version => (
                    <option key={version.id} value={version.id}>
                      v{version.version} - {new Date(version.created_at).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })} {version.is_active ? '(активная)' : ''}
                      {version.version_notes ? ` - ${version.version_notes}` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Выбор версии сразу активирует её
                </small>
              </div>
            ) : null}

            <div className="form-group">
              <label>
                {selectedItem && availableVersions.length > 0
                  ? 'Текст сообщения'
                  : 'Текст сообщения (первая версия)'}
              </label>
              <textarea
                required
                rows="8"
                value={formData.message_text || ''}
                onChange={(e) => handleFormChange('message_text', e.target.value)}
                placeholder="Текст сообщения..."
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              ></textarea>
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                {selectedItem && availableVersions.length > 0
                  ? 'Изменение текста создаст новую версию при сохранении'
                  : 'Создаст первую версию автоматически'}
              </small>
            </div>
            
            <div className="form-group">
              <label className="checkbox-item">
                <input 
                  type="checkbox" 
                  checked={formData.is_active || false} 
                  onChange={(e) => handleFormChange('is_active', e.target.checked)} 
                />
                <span>Активно</span>
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Отмена</button>
              <button type="submit" className="btn btn-primary">{selectedItem ? 'Обновить' : 'Создать'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Component for the "Prompts" tab (Agent prompts)
function QnaComponent({ onTabChange }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [formData, setFormData] = useState({});
  const [showHierarchy, setShowHierarchy] = useState(true);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [originalPromptText, setOriginalPromptText] = useState('');

  const fetchPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPrompts();
      setPrompts(data);
    } catch (error) {
      toast.error('Ошибка загрузки промптов');
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const handleEdit = async (prompt) => {
    try {
      const promptWithVersions = await api.getPrompt(prompt.id);
      setSelectedPrompt(promptWithVersions);
      setFormData({
        description: promptWithVersions.description,
        is_active: promptWithVersions.is_active,
      });

      // Загружаем все версии для dropdown
      if (promptWithVersions.versions && promptWithVersions.versions.length > 0) {
        setAvailableVersions(promptWithVersions.versions);

        // Находим активную версию
        const activeVersion = promptWithVersions.versions.find(v => v.is_active);
        if (activeVersion) {
          setSelectedVersionId(activeVersion.id);
          setOriginalPromptText(activeVersion.prompt_text);
          // Устанавливаем текст активной версии в formData
          setFormData(prev => ({ ...prev, description: activeVersion.prompt_text }));
        }
      } else {
        setAvailableVersions([]);
        setSelectedVersionId(null);
        setOriginalPromptText('');
      }

      setShowModal(true);
    } catch (error) {
      toast.error('Ошибка загрузки данных промпта');
    }
  };

  const handlePromptClick = (prompt) => {
    handleEdit(prompt);
  };

  // Обработчик выбора версии из dropdown
  const handleVersionChange = async (versionId) => {
    const versionIdNum = parseInt(versionId);
    const selectedVersion = availableVersions.find(v => v.id === versionIdNum);

    if (!selectedVersion) return;

    try {
      // Активируем выбранную версию
      await api.activatePromptVersion(selectedPrompt.id, versionIdNum);
      toast.success(`Активирована версия v${selectedVersion.version}`);

      // Обновляем состояние
      setSelectedVersionId(versionIdNum);
      setOriginalPromptText(selectedVersion.prompt_text);
      setFormData(prev => ({ ...prev, description: selectedVersion.prompt_text }));

      // Обновляем список версий (is_active флаги)
      const updatedPromptData = await api.getPrompt(selectedPrompt.id);
      setAvailableVersions(updatedPromptData.versions);
      fetchPrompts(); // Обновляем таблицу
    } catch (error) {
      toast.error(error.message || 'Ошибка активации версии');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.description || formData.description.trim() === '') {
      toast.error('Текст промпта не может быть пустым');
      return;
    }

    try {
      // Проверяем изменился ли текст промпта (только для редактирования)
      const promptTextChanged = selectedPrompt &&
                           formData.description &&
                           formData.description.trim() !== '' &&
                           formData.description.trim() !== originalPromptText.trim();

      // 1. Если изменился текст - создаем новую версию
      if (promptTextChanged) {
        await api.createPromptVersion(selectedPrompt.id, {
          prompt_text: formData.description.trim(),
          version_notes: 'Обновлено через редактор промптов'
        });
        toast.success('✅ Создана новая версия промпта');
      }

      // 2. Обновляем метаданные промпта (is_active)
      await api.updatePrompt(selectedPrompt.id, {
        is_active: formData.is_active,
      });

      if (!promptTextChanged) {
        toast.success('Промпт обновлен');
      }

      setShowModal(false);
      fetchPrompts();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении промпта');
      console.error('Error updating prompt:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <p>Загрузка промптов...</p>;
  }

  if (prompts.length === 0) {
    return (
      <div className="empty-state">
        <p>Промптов пока нет.</p>
        <p className="text-sm text-gray-500">Промпты создаются автоматически при первом запуске системы.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Схема иерархии промптов */}
      {showHierarchy && (
        <PromptHierarchy
          onPromptClick={handlePromptClick}
          prompts={prompts}
          onTabChange={onTabChange}
        />
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th className="w-10">ID</th>
              <th>Название</th>
              <th>Канал</th>
              <th>Активная версия</th>
              <th>Текст промпта (превью)</th>
              <th>Статус</th>
              <th className="w-20 text-center">Действия</th>
            </tr>
          </thead>
          <tbody>
            {prompts.map((prompt) => (
              <tr key={prompt.id}>
                <td className="text-center">{prompt.id}</td>
                <td>
                  <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                    {prompt.name}
                  </span>
                </td>
                <td className="text-center">{prompt.channel_id}</td>
                <td className="text-sm">
                  {prompt.active_version !== null && prompt.active_version !== undefined ? (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      v{prompt.active_version}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td
                  className="text-sm text-gray-600"
                  title={prompt.description}
                  style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {prompt.description.substring(0, 100)}...
                </td>
                <td>
                  <span className={`status-badge ${prompt.is_active ? 'status-active' : 'status-inactive'}`}>
                    {prompt.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="text-center">
                  <div className="table-actions">
                    <button
                      onClick={() => handleEdit(prompt)}
                      className="btn btn-outline btn-small"
                    >
                      Редактировать
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && selectedPrompt && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Редактировать промпт"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label>ID</label>
              <input
                type="text"
                disabled
                value={selectedPrompt.id}
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                ID промпта нельзя изменить
              </small>
            </div>

            <div className="form-group">
              <label>Название</label>
              <input
                type="text"
                disabled
                value={selectedPrompt.name}
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Название промпта нельзя изменить
              </small>
            </div>

            <div className="form-group">
              <label>Канал ID</label>
              <input
                type="text"
                disabled
                value={selectedPrompt.channel_id}
                style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
              />
            </div>

            {/* Если есть версии - показываем dropdown */}
            {availableVersions.length > 0 ? (
              <div className="form-group">
                <label>Версия промпта</label>
                <select
                  value={selectedVersionId || ''}
                  onChange={(e) => handleVersionChange(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                >
                  {availableVersions.map(version => (
                    <option key={version.id} value={version.id}>
                      v{version.version} - {new Date(version.created_at).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })} {version.is_active ? '(активная)' : ''}
                      {version.version_notes ? ` - ${version.version_notes}` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Выбор версии сразу активирует её
                </small>
              </div>
            ) : null}

            <div className="form-group">
              <label>
                {availableVersions.length > 0
                  ? 'Текст промпта'
                  : 'Текст промпта (первая версия)'}
              </label>
              <textarea
                required
                rows="12"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
                placeholder="Введите текст промпта для агента..."
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                {availableVersions.length > 0
                  ? 'Изменение текста создаст новую версию при сохранении'
                  : 'Создаст первую версию автоматически'}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => handleFormChange('is_active', e.target.checked)}
                />
                <span>Активен</span>
              </label>
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Только активные промпты используются системой
              </small>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                Сохранить изменения
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Component for the "Company Info Examples" tab (new table)
function CompanyInfoExamplesComponent({ onAddExampleRef }) {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);
  const [formData, setFormData] = useState({});

  // Обработчик для кнопки "Добавить пример"
  const handleAddExample = useCallback(() => {
    setSelectedExample(null);
    setFormData({
      channel_id: 2, // Автоматически устанавливаем channel_id = 2
      header1: '',
      header2: '',
      text: '',
      is_active: true,
      metadata: null,
    });
    setShowModal(true);
  }, []);

  // Передаем функцию в ref
  React.useEffect(() => {
    if (onAddExampleRef) {
      onAddExampleRef.current = handleAddExample;
    }
  }, [onAddExampleRef, handleAddExample]);

  const fetchExamples = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCompanyInfoExamples();
      setExamples(data);
    } catch (error) {
      toast.error('Ошибка загрузки примеров информации о компании');
      console.error('Error loading examples:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExamples();
  }, [fetchExamples]);

  
  const handleEdit = (example) => {
    setSelectedExample(example);
    setFormData({ ...example });
    setShowModal(true);
  };

  
  const handleDelete = async (exampleId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот пример?')) return;
    try {
      await api.deleteCompanyInfoExample(exampleId);
      toast.success('Пример удален');
      fetchExamples();
    } catch (error) {
      toast.error(error.message || 'Ошибка при удалении примера');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      channel_id: 2, // Всегда используем channel_id = 2
      header1: formData.header1 || null,
      header2: formData.header2 || null,
      text: formData.text || null,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
      metadata: formData.metadata || null,
    };

    try {
      if (selectedExample) {
        await api.updateCompanyInfoExample(selectedExample.id, payload);
        toast.success('Пример обновлен');
      } else {
        await api.createCompanyInfoExample(payload);
        toast.success('Пример создан');
      }
      setShowModal(false);
      fetchExamples();
    } catch (error) {
      toast.error(error.message || 'Ошибка при сохранении примера');
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <p>Загрузка примеров...</p>;
  }

  return (
    <div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th className="w-10">ID</th>
              <th>Категория (header1)</th>
              <th>Вопрос (header2)</th>
              <th>Ответ (text)</th>
              <th>Статус</th>
              <th className="w-20 text-center">Действия</th>
            </tr>
          </thead>
          <tbody>
            {examples.map((example) => (
              <tr key={example.id}>
                <td className="text-center">{example.id}</td>
                <td className="text-sm">
                  <div title={example.header1} style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {example.header1 || '-'}
                  </div>
                </td>
                <td className="text-sm">
                  <div title={example.header2} style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {example.header2 || '-'}
                  </div>
                </td>
                <td className="text-sm">
                  <div title={example.text} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {example.text || '-'}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${example.is_active ? 'status-active' : 'status-inactive'}`}>
                    {example.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td className="text-center">
                  <div className="table-actions">
                    <button
                      onClick={() => handleEdit(example)}
                      className="btn btn-outline btn-small"
                    >
                      Редактировать
                    </button>
                    <button
                      onClick={() => handleDelete(example.id)}
                      className="btn btn-danger btn-small"
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={selectedExample ? 'Редактировать пример' : 'Добавить пример'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedExample && (
              <div className="form-group">
                <label>ID</label>
                <input
                  type="text"
                  disabled
                  value={selectedExample.id}
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
            )}

            <div className="form-group">
              <label>Категория (Header 1)</label>
              <input
                type="text"
                value={formData.header1 || ''}
                onChange={(e) => handleFormChange('header1', e.target.value)}
                placeholder="Введите категорию"
              />
            </div>

            <div className="form-group">
              <label>Вопрос (Header 2)</label>
              <input
                type="text"
                value={formData.header2 || ''}
                onChange={(e) => handleFormChange('header2', e.target.value)}
                placeholder="Введите вопрос"
              />
            </div>

            <div className="form-group">
              <label>Ответ (Text)</label>
              <textarea
                rows="6"
                value={formData.text || ''}
                onChange={(e) => handleFormChange('text', e.target.value)}
                placeholder="Введите ответ"
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.is_active !== undefined ? formData.is_active : true}
                  onChange={(e) => handleFormChange('is_active', e.target.checked)}
                />
                <span>Активен</span>
              </label>
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Только активные примеры используются системой
              </small>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                {selectedExample ? 'Обновить' : 'Создать'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PromptsContentPage;